import { Outlet, useNavigate, useOutlet, useParams } from 'react-router-dom';
import { useQuery } from 'urql';
import { AutomationView } from '~/components/automation/AutomationView/AutomationView';
import { ErrorMessage } from '~/components/ui/Error';
import { getFragmentData, graphql } from '~/gql';
import { Status } from '~/gql/graphql';
import { JobTemplateFields } from '~/graphql/fragment/JobTemplateFields';
import { ListAutomationsDocument } from './automations._index';

// Fetch full template data (source of truth) so automation view uses accurate includeAttributes
const ListJobTemplatesDocument = graphql(`
  query ListJobTemplates($status: Status) {
    jobTemplates(status: $status) {
      id
      name
      ...JobTemplateFields
    }
  }
`);

export function Component() {
  const navigate = useNavigate();
  const params = useParams();
  const outlet = useOutlet();
  const [result] = useQuery({
    query: ListAutomationsDocument,
    requestPolicy: 'network-only', // Ensure automation data (including nested template) is fresh
  });
  const { data, fetching, stale } = result;

  const automation = data?.automations.find(
    (automation) => automation.id === params.automationId
  );

  // Independently fetch templates so we can trust template.includeAttributes
  // Use network-only to avoid cached templates without includeAttributes/jobAttributeIds fields
  const [templatesResult] = useQuery({
    query: ListJobTemplatesDocument,
    variables: { status: Status.Active },
    requestPolicy: 'network-only',
  });

  const templates = templatesResult.data?.jobTemplates;

  // Prefer template from jobTemplates list (authoritative), fall back to nested automation.template
  const automationTemplateId = automation?.template?.id;
  const templateFromListRaw = automationTemplateId
    ? templates?.find((t) => t.id === automationTemplateId)
    : undefined;

  // Extract fragment data to access the actual fields
  const templateFromList = templateFromListRaw
    ? getFragmentData(JobTemplateFields, templateFromListRaw)
    : undefined;

  // CRITICAL: Only merge if templateFromList actually has the includeAttributes/jobAttributeIds fields
  // Until codegen runs with the updated fragment, templateFromList will have undefined for these fields
  // and merging would overwrite the correct values from automation.template
  const hasAttributeFields =
    templateFromList &&
    ('includeAttributes' in templateFromList ||
      'jobAttributeIds' in templateFromList);

  const effectiveTemplate =
    hasAttributeFields && automation?.template
      ? ({
          // Keep any fields that might only exist on the automation's nested template
          ...automation.template,
          // Overwrite with authoritative fields (includeAttributes, tags, etc.)
          ...templateFromList,
          // CRITICAL: Preserve includeAttributes and jobAttributeIds if fromList doesn't have them
          // Backend bug: automation.template.includeAttributes can be null even when attributes exist
          includeAttributes:
            templateFromList.includeAttributes !== undefined
              ? templateFromList.includeAttributes
              : automation.template.includeAttributes,
          jobAttributeIds:
            templateFromList.jobAttributeIds !== undefined
              ? templateFromList.jobAttributeIds
              : automation.template.jobAttributeIds,
        } as (typeof automation)['template'])
      : automation?.template;

  const automationWithTemplate =
    automation && effectiveTemplate
      ? { ...automation, template: effectiveTemplate }
      : automation;

  // Avoid flashing a not-found error while the queries are still fetching or stale
  // We wait for both automations and templates so includeAttributes is reliable
  if (fetching || stale || templatesResult.fetching) {
    return null;
  }

  if (!automationWithTemplate) {
    console.error('[View Route] ❌ Automation not found!');
    console.error(
      '[View Route] Available IDs:',
      data?.automations.map((a) => a.id)
    );
    return (
      <ErrorMessage
        message='Automation not found'
        onBack={() => navigate('/automations')}
      />
    );
  }

  return (
    <div className='relative flex h-full'>
      <AutomationView automation={automationWithTemplate} />
      {outlet && (
        <div className='absolute z-50 h-full w-full xl:relative'>
          <Outlet context={{ automation: automationWithTemplate }} />
        </div>
      )}
    </div>
  );
}
